import { injectable } from 'inversify';
import type {
  ClinicReportDTO,
  CollectionsReportDTO,
  EcosystemUsageReportDTO,
  FinancialReportDTO,
  IReportRepository,
  ReminderAnalyticsQuery,
  ReminderAnalyticsReportDTO,
  SaasMetricsDTO,
} from '@/domain/repositories/IReportRepository';
import { httpClient } from '@/infrastructure/http/client/httpClient';

@injectable()
export class ApiReportRepository implements IReportRepository {
  async getFinancialReport(): Promise<FinancialReportDTO> {
    const response = await httpClient.get<any>('/reports/financial');
    const data = response.data ?? {};

    const totalAccounts = Number(data.totalAccounts ?? data.totalInvoices ?? 0);
    const paidAccounts = Number(data.paidAccounts ?? data.paidInvoices ?? 0);
    const pendingAccounts = Number(data.pendingAccounts ?? data.pendingInvoices ?? 0);
    const overdueAccounts = Number(data.overdueAccounts ?? data.overdueInvoices ?? 0);
    const breakdown = (data.accountStatusBreakdown ?? data.invoiceStatusBreakdown ?? []).map(
      (item: any) => ({
        status: String(item.status ?? ''),
        count: Number(item.count ?? 0),
        totalAmount: Number(item.totalAmount ?? 0),
      })
    );

    return {
      totalRevenue: Number(data.totalRevenue ?? 0),
      pendingRevenue: Number(data.pendingRevenue ?? 0),
      overdueRevenue: Number(data.overdueRevenue ?? 0),
      totalAccounts,
      paidAccounts,
      pendingAccounts,
      overdueAccounts,
      monthlyRevenue: (data.monthlyRevenue ?? []).map((item: any) => ({
        month: String(item.month ?? ''),
        revenue: Number(item.revenue ?? 0),
      })),
      accountStatusBreakdown: breakdown,
    };
  }

  async getClinicReport(): Promise<ClinicReportDTO> {
    const response = await httpClient.get<any>('/reports/clinics');
    const data = response.data ?? {};
    return {
      totalClinics: Number(data.totalClinics ?? 0),
      activeClinics: Number(data.activeClinics ?? 0),
      inactiveClinics: Number(data.inactiveClinics ?? 0),
    };
  }

  async getCollectionsReport(): Promise<CollectionsReportDTO> {
    const response = await httpClient.get<any>('/reports/collections');
    const data = response.data ?? {};
    return {
      asOf: String(data.asOf ?? new Date().toISOString()),
      totalDebtors: Number(data.totalDebtors ?? 0),
      totalOutstanding: Number(data.totalOutstanding ?? 0),
      aging: (data.aging ?? []).map((item: any) => ({
        bucket: item.bucket,
        count: Number(item.count ?? 0),
        amount: Number(item.amount ?? 0),
      })),
      debtors: (data.debtors ?? []).map((item: any) => ({
        tenantId: item.tenantId ?? null,
        clinicaId: item.clinicaId ?? null,
        nombreClinica: String(item.nombreClinica ?? ''),
        emailAdministrador: item.emailAdministrador ? String(item.emailAdministrador) : null,
        telefonoContacto: item.telefonoContacto ? String(item.telefonoContacto) : null,
        nombreDueno: item.nombreDueno ? String(item.nombreDueno) : null,
        periodo: String(item.periodo ?? ''),
        panelPagoId: Number(item.panelPagoId),
        monto: Number(item.monto ?? 0),
        deudaAcumulada: Number(item.deudaAcumulada ?? 0),
        totalAdeudado: Number(item.totalAdeudado ?? 0),
        estadoPago: String(item.estadoPago ?? ''),
        tenantEstado: item.tenantEstado ?? null,
        planAdminId: item.planAdminId ?? null,
        daysOverdue: Number(item.daysOverdue ?? 0),
        agingBucket: item.agingBucket,
        periods: (item.periods ?? []).map((p: any) => ({
          panelPagoId: Number(p.panelPagoId),
          periodo: String(p.periodo ?? ''),
          monto: Number(p.monto ?? 0),
          deudaAcumulada: Number(p.deudaAcumulada ?? 0),
          totalAdeudado: Number(p.totalAdeudado ?? 0),
          estadoPago: String(p.estadoPago ?? ''),
          daysOverdue: Number(p.daysOverdue ?? 0),
          agingBucket: p.agingBucket,
        })),
      })),
    };
  }

  async getSaasMetrics(): Promise<SaasMetricsDTO> {
    const response = await httpClient.get<any>('/reports/saas-metrics');
    const data = response.data ?? {};
    return {
      currentPeriod: String(data.currentPeriod ?? ''),
      mrr: Number(data.mrr ?? 0),
      collectedMrr: Number(data.collectedMrr ?? 0),
      efectividad: Number(data.efectividad ?? 0),
      arpa: Number(data.arpa ?? 0),
      moraPercent: Number(data.moraPercent ?? 0),
      churnRate: Number(data.churnRate ?? 0),
      trials: Number(data.trials ?? 0),
      actives: Number(data.actives ?? 0),
      suspended: Number(data.suspended ?? 0),
      nuevasMes: Number(data.nuevasMes ?? 0),
      bajasMes: Number(data.bajasMes ?? 0),
      monthlySeries: (data.monthlySeries ?? []).map((item: any) => ({
        month: String(item.month ?? ''),
        billed: Number(item.billed ?? 0),
        collected: Number(item.collected ?? 0),
        efectividad: Number(item.efectividad ?? 0),
        accounts: Number(item.accounts ?? 0),
        paidAccounts: Number(item.paidAccounts ?? 0),
      })),
    };
  }

  async getEcosystemUsage(period?: string): Promise<EcosystemUsageReportDTO> {
    const query = period ? `?period=${encodeURIComponent(period)}` : '';
    const response = await httpClient.get<any>(`/reports/ecosystem-usage${query}`);
    const data = response.data ?? {};
    return {
      period: String(data.period ?? ''),
      historyPeriods: Array.isArray(data.historyPeriods)
        ? data.historyPeriods.map(String)
        : [],
      totals: {
        tenants: Number(data.totals?.tenants ?? 0),
        whatsappMessages: Number(data.totals?.whatsappMessages ?? 0),
        emailMessages: Number(data.totals?.emailMessages ?? 0),
        appPatientDevices: Number(data.totals?.appPatientDevices ?? 0),
        tenantsWithWhatsappUsage: Number(data.totals?.tenantsWithWhatsappUsage ?? 0),
        tenantsWithEmailUsage: Number(data.totals?.tenantsWithEmailUsage ?? 0),
        tenantsWithAppUsage: Number(data.totals?.tenantsWithAppUsage ?? 0),
      },
      monthlySeries: (data.monthlySeries ?? []).map((item: any) => ({
        period: String(item.period ?? ''),
        whatsappMessages: Number(item.whatsappMessages ?? 0),
        emailMessages: Number(item.emailMessages ?? 0),
      })),
      featureAdoption: (data.featureAdoption ?? []).map((item: any) => ({
        featureKey: String(item.featureKey ?? ''),
        tenantsWithFeature: Number(item.tenantsWithFeature ?? 0),
      })),
      tenants: (data.tenants ?? []).map((item: any) => ({
        tenantId: String(item.tenantId ?? ''),
        nombre: String(item.nombre ?? ''),
        estado: String(item.estado ?? ''),
        planAdminId: item.planAdminId ?? null,
        whatsappMessages: Number(item.whatsappMessages ?? 0),
        whatsappMonthlyLimit: Number(item.whatsappMonthlyLimit ?? 0),
        whatsappBonus: Number(item.whatsappBonus ?? 0),
        conexionMonthlyPrice: Number(item.conexionMonthlyPrice ?? 0),
        whatsappOverageMonthlyPrice: Number(item.whatsappOverageMonthlyPrice ?? 0),
        emailMessages: Number(item.emailMessages ?? 0),
        appPatientDevices: Number(item.appPatientDevices ?? 0),
        featuresEnabled: Array.isArray(item.featuresEnabled)
          ? item.featuresEnabled.map(String)
          : [],
        monthlyHistory: (item.monthlyHistory ?? []).map((h: any) => ({
          period: String(h.period ?? ''),
          whatsappMessages: Number(h.whatsappMessages ?? 0),
          emailMessages: Number(h.emailMessages ?? 0),
        })),
        offices: Array.isArray(item.offices)
          ? item.offices.map((office: any) => ({
              officeId:
                office?.officeId == null || office.officeId === ''
                  ? null
                  : Number.isFinite(Number(office.officeId))
                    ? Number(office.officeId)
                    : null,
              officeName: String(office?.officeName ?? ''),
              whatsappMessages: Number(office?.whatsappMessages ?? 0),
              emailMessages: Number(office?.emailMessages ?? 0),
              monthlyHistory: (office?.monthlyHistory ?? []).map((h: any) => ({
                period: String(h.period ?? ''),
                whatsappMessages: Number(h.whatsappMessages ?? 0),
                emailMessages: Number(h.emailMessages ?? 0),
              })),
            }))
          : [],
      })),
    };
  }

  async getReminderAnalytics(query: ReminderAnalyticsQuery = {}): Promise<ReminderAnalyticsReportDTO> {
    const params = new URLSearchParams();
    if (query.from) params.set('from', query.from);
    if (query.to) params.set('to', query.to);
    if (query.grain) params.set('grain', query.grain);
    if (query.tenantId) params.set('tenantId', query.tenantId);
    if (query.preset) params.set('preset', query.preset);
    if (query.includeOutcomes === false) params.set('includeOutcomes', '0');
    const qs = params.toString() ? `?${params.toString()}` : '';
    const response = await httpClient.get<any>(`/reports/reminder-analytics${qs}`);
    const data = response.data ?? {};
    const o = data.outcomes ?? {};
    const ro = o.rescheduledByOrigin ?? {};
    return {
      filters: {
        from: String(data.filters?.from ?? ''),
        to: String(data.filters?.to ?? ''),
        fromUtc: String(data.filters?.fromUtc ?? ''),
        toUtcExclusive: String(data.filters?.toUtcExclusive ?? ''),
        grain:
          data.filters?.grain === 'week' || data.filters?.grain === 'month'
            ? data.filters.grain
            : 'day',
        tenantId: data.filters?.tenantId ? String(data.filters.tenantId) : null,
        tenantName: data.filters?.tenantName ? String(data.filters.tenantName) : null,
        includeOutcomeScan: Boolean(data.filters?.includeOutcomeScan),
      },
      totals: {
        whatsapp: Number(data.totals?.whatsapp ?? 0),
        email: Number(data.totals?.email ?? 0),
        push: Number(data.totals?.push ?? 0),
        total: Number(data.totals?.total ?? 0),
        failed: Number(data.totals?.failed ?? 0),
        pending: Number(data.totals?.pending ?? 0),
      },
      outcomes: {
        confirmedWhatsapp: Number(o.confirmedWhatsapp ?? 0),
        confirmedApp: Number(o.confirmedApp ?? 0),
        confirmedEmail: Number(o.confirmedEmail ?? 0),
        confirmedTotal: Number(o.confirmedTotal ?? 0),
        rejectedWhatsapp: Number(o.rejectedWhatsapp ?? 0),
        rejectedApp: Number(o.rejectedApp ?? 0),
        rejectedEmail: Number(o.rejectedEmail ?? 0),
        rejectedStaff: Number(o.rejectedStaff ?? 0),
        rejectedOther: Number(o.rejectedOther ?? 0),
        rejectedTotal: Number(o.rejectedTotal ?? 0),
        rescheduled: Number(o.rescheduled ?? 0),
        rescheduledByOrigin: {
          app: Number(ro.app ?? 0),
          whatsapp: Number(ro.whatsapp ?? 0),
          email: Number(ro.email ?? 0),
          staff: Number(ro.staff ?? 0),
          other: Number(ro.other ?? 0),
        },
      },
      series: (data.series ?? []).map((item: any) => ({
        period: String(item.period ?? ''),
        whatsapp: Number(item.whatsapp ?? 0),
        email: Number(item.email ?? 0),
        push: Number(item.push ?? 0),
        total: Number(item.total ?? 0),
        confirmedWhatsapp: Number(item.confirmedWhatsapp ?? 0),
        rejectedWhatsapp: Number(item.rejectedWhatsapp ?? 0),
        rejectedApp: Number(item.rejectedApp ?? 0),
        rescheduled: Number(item.rescheduled ?? 0),
      })),
      tenants: (data.tenants ?? []).map((item: any) => ({
        tenantId: String(item.tenantId ?? ''),
        nombre: String(item.nombre ?? ''),
        estado: String(item.estado ?? ''),
        whatsapp: Number(item.whatsapp ?? 0),
        email: Number(item.email ?? 0),
        push: Number(item.push ?? 0),
        total: Number(item.total ?? 0),
        confirmedWhatsapp: Number(item.confirmedWhatsapp ?? 0),
        rejectedWhatsapp: Number(item.rejectedWhatsapp ?? 0),
        rejectedApp: Number(item.rejectedApp ?? 0),
        rescheduled: Number(item.rescheduled ?? 0),
      })),
      notes: Array.isArray(data.notes) ? data.notes.map(String) : [],
      tenantOutcomesScanned: Number(data.tenantOutcomesScanned ?? 0),
      tenantOutcomesFailed: Number(data.tenantOutcomesFailed ?? 0),
    };
  }
}
